import { useEffect, useRef } from "react";

const DEFAULT_INTERVAL_MS = 5 * 60_000;
const DEFAULT_MINIMUM_GAP_MS = 10_000;
const DEFAULT_INTERACTION_WINDOW_MS = 6 * 60_000;

interface VisibleLiveRefreshOptions {
  readonly enabled?: boolean;
  readonly intervalMs?: number;
  readonly minimumGapMs?: number;
  readonly interactionWindowMs?: number;
}

/**
 * Refresh mounted live data only while the document is visible and recently used.
 * Focus/visibility recovery shares the same minimum gap as the periodic refresh.
 */
export function useVisibleLiveRefresh(
  refresh: () => void,
  options: VisibleLiveRefreshOptions = {},
): void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (options.enabled === false) return;
    const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    const minimumGapMs = options.minimumGapMs ?? DEFAULT_MINIMUM_GAP_MS;
    const interactionWindowMs = options.interactionWindowMs ?? DEFAULT_INTERACTION_WINDOW_MS;
    let lastInteractionAt = Date.now();
    let lastRefreshAt = Date.now();

    const recordInteraction = () => {
      lastInteractionAt = Date.now();
    };
    const refreshAfterMinimumGap = () => {
      const now = Date.now();
      if (document.visibilityState !== "visible") return;
      if (now - lastRefreshAt < minimumGapMs) return;
      lastRefreshAt = now;
      refreshRef.current();
    };
    const refreshIfRecentlyActive = () => {
      if (Date.now() - lastInteractionAt > interactionWindowMs) return;
      refreshAfterMinimumGap();
    };
    const refreshAfterReturn = () => {
      recordInteraction();
      refreshAfterMinimumGap();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshAfterReturn();
    };

    window.addEventListener("focus", refreshAfterReturn);
    window.addEventListener("keydown", recordInteraction);
    window.addEventListener("pointerdown", recordInteraction);
    window.addEventListener("pointermove", recordInteraction);
    window.addEventListener("wheel", recordInteraction);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = window.setInterval(refreshIfRecentlyActive, intervalMs);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshAfterReturn);
      window.removeEventListener("keydown", recordInteraction);
      window.removeEventListener("pointerdown", recordInteraction);
      window.removeEventListener("pointermove", recordInteraction);
      window.removeEventListener("wheel", recordInteraction);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [options.enabled, options.interactionWindowMs, options.intervalMs, options.minimumGapMs]);
}
